import React, { useMemo, useState } from 'react';

import {
  Avatar,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FragmentType, graphql, useFragment } from '../../__generated__';
import { getBalanceInfo } from '../../util/balance';

const ITEM_HEIGHT = 48;

export const SatelliteAgentListItemFragment = graphql(/* GraphQL */ `
  fragment SatelliteAgentListItemFragment on Agent {
    id
    tenant
    name
    limit
    balance {
      label
      symbol
      name
      value
    }
    phone
    email
  }
`);

type SatelliteAgentListItemProps = {
  agent: FragmentType<typeof SatelliteAgentListItemFragment>;
  onEdit: () => void;
  onDelete: () => void;
  onResetBalance: () => void;
  setSelectedAgent: (
    agent: FragmentType<typeof SatelliteAgentListItemFragment>
  ) => void;
};

const SatelliteAgentItem = (
  props: SatelliteAgentListItemProps
): JSX.Element => {
  const { onEdit, onDelete, setSelectedAgent, onResetBalance } = props;
  const agent = useFragment(SatelliteAgentListItemFragment, props.agent);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgent(props.agent);
  };

  const onOpenEdit = () => {
    handleClose();
    onEdit();
  };

  const onOpenDelete = () => {
    handleClose();
    onDelete();
  };

  const onOpenRestoreBalance = () => {
    handleClose();
    onResetBalance();
  };

  let limitText: string;
  let balanceAmount: number | null = null;
  if (agent?.limit) {
    if (
      agent?.balance &&
      agent.balance.length > 0 &&
      agent.balance[0] !== null
    ) {
      const balance = getBalanceInfo(agent.balance[0]);
      limitText = `${balance.amount} of ${balance.symbol}${agent.limit} available`;
      balanceAmount = agent.balance[0].value;
    } else {
      limitText = `Limit set to ${agent.limit}`;
    }
  } else {
    limitText = 'Limits not yet set';
  }
  const hasLimit = useMemo(() => {
    return Boolean(agent?.limit && agent?.limit > 0);
  }, [agent.limit]);

  return (
    <ListItem
      alignItems="flex-start"
      secondaryAction={
        <React.Fragment>
          <IconButton
            edge="end"
            aria-label="more"
            id="long-button"
            aria-controls={open ? 'long-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={(e) => handleClick(e)}
            color={'primary'}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="long-menu"
            MenuListProps={{
              'aria-labelledby': 'long-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: -140,
            }}
            slotProps={{
              paper: {
                style: {
                  maxHeight: ITEM_HEIGHT * 4.5,
                },
              },
            }}
          >
            <MenuItem key={'edit'} onClick={onOpenEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              Edit
            </MenuItem>
            <MenuItem key={'delete'} onClick={onOpenDelete}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              Delete
            </MenuItem>
            {hasLimit && balanceAmount && (agent?.limit || 0) > balanceAmount && (
              <MenuItem key={'restore'} onClick={onOpenRestoreBalance}>
                <ListItemIcon>
                  <RefreshIcon fontSize="small" />
                </ListItemIcon>
                Restore Balance
              </MenuItem>
            )}
          </Menu>
        </React.Fragment>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <PersonIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={agent.name}
        secondary={
          <React.Fragment>
            <Typography variant="body2" color="text.secondary">
              {limitText}
            </Typography>
          </React.Fragment>
        }
      />
    </ListItem>
  );
};

export default SatelliteAgentItem;
